import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from './CartContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import BannerComponent from './BannerComponent';
import { fetchBannerSettings, getBannerText } from '../api/woocommerce';

const Header = () => {
  const { cart, openCart, openSearch, openMobileMenu } = useCart();
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  // Add banner state
  const [bannerSettings, setBannerSettings] = useState(null);
  const [isLoadingBanner, setIsLoadingBanner] = useState(false);
  const loadedLanguageRef = useRef(null);

  // Add useEffect to fetch banner settings with caching
  useEffect(() => {
    const loadBannerSettings = async () => {
      // Skip if already loading or if we already have data for this language
      if (isLoadingBanner || loadedLanguageRef.current === currentLanguage) {
        return;
      }

      try {
        setIsLoadingBanner(true);
        const bannerData = await fetchBannerSettings(currentLanguage);
        setBannerSettings(bannerData);
        loadedLanguageRef.current = currentLanguage;
      } catch (err) {
        console.error('Error loading banner settings:', err);
      } finally {
        setIsLoadingBanner(false);
      }
    };

    loadBannerSettings();
  }, [currentLanguage, isLoadingBanner]);

  const handleMobileMenuClick = (e) => {
    e.preventDefault();
    openMobileMenu();
  };

  const handleCartClick = (e) => {
    e.preventDefault();
    openCart();
  };

  const handleSearchClick = (e) => {
    e.preventDefault();
    openSearch();
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-white">
      <div className="pl-[6px] pr-2">
        <header className='pt-2.5' >
          <div className="w-full flex justify-between items-center">
            <Link to="/" className="text-18 font-light md:pb-[4px] pb-[5px]">Institute Lab</Link>

            {/* Desktop Navigation - commented out for reference */}
            {/* 
            <nav className="hidden md:flex items-center">
              <div className='mb-[9px]'>
                <Link to="/" className="text-14 font-light mr-2">{t('header.home')}</Link>
                <Link to="/products" className="text-14 font-medium mr-4">{t('header.products')}</Link>
              </div>
              <div className=' mb-[10px] h-[16px]'>
                <button
                  className="focus:outline-none mr-2"
                  aria-label={t('search.placeholder')}
                  onClick={handleSearchClick}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#clip0_2005_226)">
                      <path d="M6.90909 12.7273C10.1224 12.7273 12.7273 10.1224 12.7273 6.90909C12.7273 3.6958 10.1224 1.09091 6.90909 1.09091C3.6958 1.09091 1.09091 3.6958 1.09091 6.90909C1.09091 10.1224 3.6958 12.7273 6.90909 12.7273Z" stroke="black" strokeMiterlimit="10" />
                      <path d="M11.2727 11.2727L14.9091 14.9091" stroke="black" strokeMiterlimit="10" />
                    </g>
                    <defs>
                      <clipPath id="clip0_2005_226">
                        <rect width="16" height="16" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </button>
                <button
                  className="focus:outline-none relative"
                  onClick={handleCartClick}
                  aria-label="{t('cart.title')}"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#clip0_2002_191)">
                      <path d="M10.0582 4C10.1382 3.77455 10.1818 3.52727 10.1818 3.27273C10.1818 2.06546 9.20727 1.09091 8 1.09091C6.79273 1.09091 5.81818 2.06546 5.81818 3.27273C5.81818 3.52727 5.86182 3.77455 5.94182 4" stroke="black" strokeMiterlimit="10" />
                      <path fillRule="evenodd" clipRule="evenodd" d="M1.09091 14.9091H14.9091L13.4545 4H2.54546L1.09091 14.9091Z" stroke="black" strokeMiterlimit="10" />
                      <path d="M5.81818 6.18182C5.81818 7.38909 6.79273 8.36364 8 8.36364C9.20727 8.36364 10.1818 7.38909 10.1818 6.18182" stroke="black" strokeMiterlimit="10" />
                    </g>
                    <defs>
                      <clipPath id="clip0_2002_191">
                        <rect width="16" height="16" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  {cart.totalItems > 0 && (
                    <span className="absolute -bottom-0.5 -right-0.5 w-[8px] h-[8px] flex items-center justify-center">
                      <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <ellipse cx="4" cy="3.99846" rx="4" ry="3.99846" fill="#F96239" />
                      </svg>
                    </span>
                  )}
                </button>
              </div>
            </nav>
            */}

            {/* Mobile-style Navigation - now visible on both mobile and desktop */}
            <div className="flex items-center space-x-2 pb-[10px]">
              <button
                className="text-sm focus:outline-none"
                onClick={handleMobileMenuClick}
                aria-label="Menu"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_8008_315)">
                    <path d="M0.727295 5.45459H15.2727" stroke="black" strokeMiterlimit="10" />
                    <path d="M0.727295 10.5454H15.2727" stroke="black" strokeMiterlimit="10" />
                  </g>
                  <defs>
                    <clipPath id="clip0_8008_315">
                      <rect width="16" height="16" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </button>
              
              {/* Search button - visible only on desktop */}
              <button
                className="hidden md:block focus:outline-none"
                aria-label={t('search.placeholder')}
                onClick={handleSearchClick}
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_2005_226)">
                    <path d="M6.90909 12.7273C10.1224 12.7273 12.7273 10.1224 12.7273 6.90909C12.7273 3.6958 10.1224 1.09091 6.90909 1.09091C3.6958 1.09091 1.09091 3.6958 1.09091 6.90909C1.09091 10.1224 3.6958 12.7273 6.90909 12.7273Z" stroke="black" strokeMiterlimit="10" />
                    <path d="M11.2727 11.2727L14.9091 14.9091" stroke="black" strokeMiterlimit="10" />
                  </g>
                  <defs>
                    <clipPath id="clip0_2005_226">
                      <rect width="16" height="16" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </button>

              <button
                className="focus:outline-none relative"
                onClick={handleCartClick}
                aria-label="{t('cart.title')}"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clipPath="url(#clip0_2002_191)">
                    <path d="M10.0582 4C10.1382 3.77455 10.1818 3.52727 10.1818 3.27273C10.1818 2.06546 9.20727 1.09091 8 1.09091C6.79273 1.09091 5.81818 2.06546 5.81818 3.27273C5.81818 3.52727 5.86182 3.77455 5.94182 4" stroke="black" strokeMiterlimit="10" />
                    <path fillRule="evenodd" clipRule="evenodd" d="M1.09091 14.9091H14.9091L13.4545 4H2.54546L1.09091 14.9091Z" stroke="black" strokeMiterlimit="10" />
                    <path d="M5.81818 6.18182C5.81818 7.38909 6.79273 8.36364 8 8.36364C9.20727 8.36364 10.1818 7.38909 10.1818 6.18182" stroke="black" strokeMiterlimit="10" />
                  </g>
                  <defs>
                    <clipPath id="clip0_2002_191">
                      <rect width="16" height="16" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
                {cart.totalItems > 0 && (
                  <span className="absolute -bottom-0.5 -right-0.5 w-[8px] h-[8px] flex items-center justify-center">
                    <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <ellipse cx="4" cy="3.99846" rx="4" ry="3.99846" fill="#F96239" />
                    </svg>
                  </span>
                )}
              </button>
            </div>
          </div>
          {bannerSettings?.enabled && !isLoadingBanner &&
            <div className='ml-[2px]'>
              {/* Conditional separator */}
              {getBannerText(bannerSettings, currentLanguage) && (
                <hr className='border-t border-black ' />
              )}
              {/* Banner Component */}
              <BannerComponent bannerSettings={bannerSettings} currentLanguage={currentLanguage} />
            </div>}
          <hr className='border-t border-black ml-[2px]' />
        </header>
      </div>
    </div>
  );
};

export default Header;